import { Badge } from "../../../components/ui/badge";
import { DASH, formatNumber, formatSigned, humanize, sentimentLabelFor, sentimentText, sentimentTone } from "../format";
import type { SentimentResult } from "../types";
import { Block, Empty, Stat, Unavailable, type TurnRef } from "./shared";

export function SentimentPanel({ result, turnRef }: { result: SentimentResult; turnRef: TurnRef }) {
  const { overall, trend, availability: av } = result;
  const label = overall?.label ?? sentimentLabelFor(overall?.score);

  return (
    <div className="space-y-5">
      <p className="text-xs text-ink-3">
        Customer turns only, on a −1 to +1 scale. It estimates intensity; it is not a probability.
      </p>
      {!av.audio ? <Unavailable>Voice was not used: {av.audio_reason || "no usable recording."} The score comes from the words alone.</Unavailable> : null}
      {!av.text ? <Unavailable>The transcript could not be scored.</Unavailable> : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat
          label="Overall"
          value={<Badge tone={sentimentTone(label)}>{sentimentText(overall?.score, label)}</Badge>}
          hint={`confidence ${formatNumber(overall?.confidence, 2)}`}
        />
        <Stat label="Words" value={formatSigned(overall?.text_score)} />
        <Stat label="Voice" value={formatSigned(overall?.audio_score)} hint={overall?.agreement ? `${humanize(overall.agreement)} agreement` : undefined} />
        <Stat label="Post-call label" value={humanize(result.pca_label)} hint="for comparison" />
      </div>

      <Block title="Trend">
        {trend ? (
          <div className="space-y-1 text-sm text-ink-2">
            <p>
              {humanize(trend.direction)}: {formatSigned(trend.start_score)} → {formatSigned(trend.end_score)} (change {formatSigned(trend.delta)})
            </p>
            {trend.turning_points.length ? (
              <p className="text-xs text-ink-3">Turning points: {trend.turning_points.map(turnRef).join(", ")}</p>
            ) : null}
          </div>
        ) : <Empty>{DASH}</Empty>}
        {result.segments.length ? (
          <ol className="flex flex-wrap gap-2" aria-label="Sentiment by part of the call">
            {result.segments.map((segment) => (
              <li key={segment.part} className="rounded-lg border border-line bg-canvas px-3 py-1.5 text-sm">
                <span className="text-ink-3">{humanize(segment.part)}</span>{" "}
                <span className="font-medium tabular-nums text-ink">{formatSigned(segment.score)}</span>
              </li>
            ))}
          </ol>
        ) : null}
      </Block>

      <Block title="Customer turns">
        {result.turns.length ? (
          <ul className="divide-y divide-line rounded-xl border border-line">
            {result.turns.map((turn) => {
              const turnLabel = turn.label ?? sentimentLabelFor(turn.score);
              return (
                <li key={turn.turn_index} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-28 shrink-0 text-xs text-ink-3">{turnRef(turn.turn_index)}</span>
                  <Badge tone={sentimentTone(turnLabel)}>{sentimentText(turn.score, turnLabel)}</Badge>
                  <span className="text-xs text-ink-3">words {formatSigned(turn.text_score)} · voice {formatSigned(turn.audio_score)}</span>
                  {turn.key_phrases.length ? (
                    <span className="text-xs text-ink-2">{turn.key_phrases.map((p) => `“${p}”`).join(", ")}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : <Empty>No customer turns were scored.</Empty>}
      </Block>
    </div>
  );
}
